import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Spin } from 'antd'

import { authProvider } from './authProvider'
import { urqlClient } from './lib/urqlClient'
import { buildLoginRedirect } from './features/auth/utils/loginRedirect'

type GuardState =
  | { status: 'pending' }
  | { status: 'authenticated' }
  | { status: 'redirect'; to: string }

export const AuthGuard: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const location = useLocation()
  const [state, setState] = useState<GuardState>({ status: 'pending' })

  useEffect(() => {
    let cancelled = false
    authProvider(urqlClient)
      .check()
      .then((result) => {
        if (cancelled) return
        if (result.authenticated) {
          setState({ status: 'authenticated' })
        } else {
          const reason =
            typeof result.redirectTo === 'string' &&
            result.redirectTo.includes('reason=')
              ? result.redirectTo.split('?')[1]
              : 'expired'
          setState({
            status: 'redirect',
            to: buildLoginRedirect(reason, location.pathname + location.search),
          })
        }
      })
      .catch(() => {
        if (cancelled) return
        setState({
          status: 'redirect',
          to: buildLoginRedirect(
            'expired',
            location.pathname + location.search,
          ),
        })
      })
    return () => {
      cancelled = true
    }
  }, [location.pathname, location.search])

  if (state.status === 'pending') {
    return (
      <div
        style={{
          display: 'flex',
          minHeight: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spin size="large" />
      </div>
    )
  }

  if (state.status === 'redirect') {
    return <Navigate to={state.to} replace />
  }

  return <>{children}</>
}
